/**
 * Contracts view: choose a deployed contract and a method from the node's catalogue, enter the
 * parameters, review, sign and submit. Templates: call-form-template, call-review-template,
 * no-catalogue-template. The result of the last call is a panel above the form while txid is set; it
 * closes from its header, on the next review and when the user leaves the tab.
 */

import ko from 'knockout';
import { ModelBase } from './base';
import type { WalletModel } from './wallet';
import type { ContractInfo, PendingCall } from '../transactions';
import { FEE, contractLabel, fetchContractCatalogue, reviewCall, submitContractCall } from '../transactions';

export class ContractsModel extends ModelBase {
    readonly fee = FEE;

    // catalogue
    readonly contracts = ko.observableArray<ContractInfo>([]);
    readonly catalogueError = ko.observable('');
    readonly cid = ko.observable('');
    readonly contract = ko.pureComputed(() => this.contracts().find(c => c.cid === this.cid()));
    readonly methods = ko.pureComputed(() => this.contract()?.methods ?? []);

    // form
    readonly method = ko.observable('');
    readonly params = ko.observable('');
    readonly amount = ko.observable('0');

    // review and result
    readonly pending = ko.observable<PendingCall | null>(null);
    readonly pendingParams = ko.pureComputed(() => {
        const p = this.pending();
        return p && p.params ? JSON.stringify(p.params, null, 2) : 'none';
    });
    readonly busy = ko.observable(false);
    /** The last call's transaction id; the result panel is shown while it is set. */
    readonly txid = ko.observable('');
    /** The node answered that the transaction is in a block; otherwise it is still being processed. */
    readonly included = ko.observable(false);

    constructor(private readonly wallet: WalletModel) {
        super();
        this.template('call-form-template');
    }

    label(contract: ContractInfo): string {
        return contractLabel(contract);
    }

    /** Reloads the catalogue from the node; without one the view says why instead of showing the form. */
    async refreshCatalogue(): Promise<void> {
        try {
            this.contracts(await fetchContractCatalogue());
            this.catalogueError('');
            if (!this.contract()) this.cid(this.contracts()[0]?.cid ?? '');
        } catch (err) {
            this.contracts([]);
            this.catalogueError(err instanceof Error ? err.message : String(err));
        }
        if (this.template() === 'call-form-template' || this.template() === 'no-catalogue-template') {
            this.template(this.contracts().length > 0 ? 'call-form-template' : 'no-catalogue-template');
        }
    }

    review(): void {
        this.hideErrors();
        this.dismissResult();
        try {
            this.pending(reviewCall({ contract: this.contract(), method: this.method(), params: this.params(), amount: this.amount() }));
            this.template('call-review-template');
        } catch (err) {
            this.displayErrors(err);
        }
    }

    async confirm(): Promise<void> {
        const pending = this.pending();
        if (!pending) return;
        this.hideErrors();
        this.busy(true);
        try {
            const result = await submitContractCall(pending);
            this.txid(result.txid);
            this.included(result.status === 'included');
            this.pending(null);
            this.params('');
            this.amount('0');
            this.template('call-form-template');
            this.wallet.expectAccountChange();
        } catch (err) {
            this.displayErrors(err);
        } finally {
            this.busy(false);
        }
    }

    cancel(): void {
        this.pending(null);
        this.template('call-form-template');
    }

    /** Closes the result panel. */
    dismissResult(): void {
        this.txid('');
        this.included(false);
    }

    /** The user left the tab or locked the wallet: no result, no error, and a review goes back to the form with its values. */
    leave(): void {
        if (this.busy()) return;
        this.dismissResult();
        this.hideErrors();
        if (this.template() === 'call-review-template') this.cancel();
    }
}
